import React, { useState } from 'react';
import { Code, ChevronDown, ChevronRight, X, BarChart2, AlertTriangle, Clock } from 'lucide-react';
import { debugEvents } from '../services/events';

interface ApiRequest {
  id: string;
  timestamp: string;
  provider: 'openai' | 'groq';
  request: any;
  response?: any;
  error?: any;
  metrics?: {
    totalQuestionsCompared: number;
    successfulComparisons?: number;
    error?: boolean;
  };
}

export function ApiDebugPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [requests, setRequests] = useState<ApiRequest[]>([]);

  React.useEffect(() => {
    const handleApiRequest = (event: Event) => {
      const customEvent = event as CustomEvent<ApiRequest>;
      setRequests(prev => [...prev, customEvent.detail]);
    };

    debugEvents.addEventListener('api-request', handleApiRequest);
    return () => {
      debugEvents.removeEventListener('api-request', handleApiRequest);
    };
  }, []);

  if (requests.length === 0) return null;

  return (
    <div className="fixed bottom-4 left-4 z-50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="rounded-full w-12 h-12 p-0 bg-blue-600 text-white shadow-lg hover:bg-blue-700 flex items-center justify-center"
      >
        <Code className="w-5 h-5" />
      </button>

      {isOpen && (
        <div className="fixed inset-x-4 bottom-20 top-20 bg-gray-900 text-white rounded-lg shadow-xl overflow-hidden flex flex-col">
          <div className="p-4 border-b border-gray-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Code className="w-5 h-5" />
              <span>API Debug Panel ({requests.length} requests)</span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {requests.map((request) => (
              <div key={request.id} className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400">
                      <Clock className="w-4 h-4 inline mr-1" />
                      {new Date(request.timestamp).toLocaleTimeString()}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      request.provider === 'openai' ? 'bg-green-900 text-green-200' : 'bg-purple-900 text-purple-200'
                    }`}>
                      {request.provider}
                    </span>
                  </div>
                  {request.error && (
                    <span className="text-xs px-2 py-1 rounded bg-red-900 text-red-200 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Error
                    </span>
                  )}
                </div>

                {request.metrics && (
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gray-700/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <BarChart2 className="w-4 h-4 text-blue-400" />
                        <span className="text-xs font-medium text-gray-300">Questions Compared</span>
                      </div>
                      <span className="text-2xl font-bold text-blue-400">
                        {request.metrics.totalQuestionsCompared}
                      </span>
                    </div>
                    
                    {request.metrics.successfulComparisons !== undefined && (
                      <div className="bg-gray-700/50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <BarChart2 className="w-4 h-4 text-green-400" />
                          <span className="text-xs font-medium text-gray-300">Successful Matches</span>
                        </div>
                        <span className="text-2xl font-bold text-green-400">
                          {request.metrics.successfulComparisons}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-gray-400 mb-1">Request:</div>
                    <pre className="text-sm bg-gray-900/50 p-3 rounded-lg overflow-x-auto">
                      <code>{JSON.stringify(request.request, null, 2)}</code>
                    </pre>
                  </div>

                  {request.response && (
                    <div>
                      <div className="text-xs text-gray-400 mb-1">Response:</div>
                      <pre className="text-sm bg-gray-900/50 p-3 rounded-lg overflow-x-auto">
                        <code className="text-green-300">
                          {JSON.stringify(request.response, null, 2)}
                        </code>
                      </pre>
                    </div>
                  )}

                  {request.error && (
                    <div>
                      <div className="text-xs text-gray-400 mb-1">Error:</div>
                      <pre className="text-sm bg-gray-900/50 p-3 rounded-lg overflow-x-auto">
                        <code className="text-red-300">
                          {JSON.stringify(request.error, null, 2)}
                        </code>
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}